import React, { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useResetPassword } from '@/hooks/use-resetpassword';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, ArrowLeft, Lock, Mail, Key, Eye, EyeOff } from 'lucide-react';
import { Header } from '@/components/header';
import { Footer } from '@/components/footer';
import { useAuth } from '@/context/AuthContext';
import { Toast, ToastProvider, ToastViewport } from "@/components/ui/toast";

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);
  const { isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [, navigate] = useLocation();
  const { 
    requestPasswordReset, 
    resetPassword: resetPasswordFn, 
    loading, 
    error, 
    success, 
    otpSent, 
    setOtpSent 
  } = useResetPassword();

  // Show toast on error
  React.useEffect(() => {
    if (error) {
      setToastMessage(error);
      setToastOpen(true);
    }
  }, [error]);

  // Show success message
  React.useEffect(() => {
    if (success && !otpSent) {
      setToastMessage(t('forgotPassword.resetSuccess'));
      setToastOpen(true);
    }
  }, [success, otpSent]);

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    await requestPasswordReset(email);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      alert(t('forgotPassword.passwordsDontMatch'));
      return;
    }
    const resetSuccessful = await resetPasswordFn(email, otp, newPassword);
    if (resetSuccessful) {
      // Redirect to login after a short delay
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white dark:bg-slate-900">
      <ToastProvider>
        <ToastViewport className="fixed top-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-md px-4" />
        <Toast
          open={toastOpen}
          onOpenChange={setToastOpen}
          className="animate-in slide-in-from-top-8 fade-in-0 data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom-8 data-[state=closed]:fade-out-0 duration-600"
        >
          <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-100 p-4 rounded-md shadow-lg">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm font-medium">{toastMessage}</p>
          </div>
        </Toast>
      </ToastProvider>

      <Header isLoggedIn={isAuthenticated} />
      
      <main className="flex-grow py-8 md:py-12 lg:py-16 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <Card className="border border-slate-200 dark:border-slate-700 shadow-xl bg-white dark:bg-slate-800">
            <CardHeader className="space-y-1 text-center">
              <div className="mx-auto bg-slate-100 dark:bg-slate-700 p-3 rounded-full w-16 h-16 flex items-center justify-center mb-2">
                <Lock className="w-8 h-8 text-slate-600 dark:text-slate-300" />
              </div>
              <CardTitle className="text-2xl font-bold">
                {otpSent ? t('forgotPassword.resetTitle') : t('forgotPassword.title')}
              </CardTitle>
              <CardDescription className="text-slate-600 dark:text-slate-300">
                {otpSent 
                  ? t('forgotPassword.otpSentMessage')
                  : t('forgotPassword.forgotPasswordMessage')}
              </CardDescription>
            </CardHeader>
            
            <form onSubmit={otpSent ? handleResetPassword : handleRequestOtp}>
              <CardContent className="space-y-4">

                {!otpSent ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('forgotPassword.emailLabel')}</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="email"
                          placeholder={t('forgotPassword.emailPlaceholder')}
                          type="email"
                          autoCapitalize="none"
                          autoComplete="email"
                          autoCorrect="off"
                          disabled={loading}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10 h-11"
                          required
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white" 
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('forgotPassword.sending')}
                        </>
                      ) : t('forgotPassword.sendOtp')}
                    </Button>
                    <div className="text-center text-sm text-slate-600 dark:text-slate-400">
                     {t('forgotPassword.rememberPassword')}{' '}
                      <Link href="/login" className="text-blue-600 hover:underline dark:text-blue-400">
                        {t('forgotPassword.signIn')}
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="otp" className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('forgotPassword.verificationCode')}</Label>
                      <div className="relative">
                        <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="otp"
                          placeholder={t('forgotPassword.otpPlaceholder')}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={6}
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                          className="pl-10 h-11"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="newPassword" className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('forgotPassword.newPassword')}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="newPassword"
                          type={showPassword ? 'text' : 'password'}
                          placeholder={t('forgotPassword.newPasswordPlaceholder')}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="pl-10 pr-10 h-11"
                          required
                          minLength={8}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={togglePasswordVisibility}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-slate-500" />
                          ) : (
                            <Eye className="h-4 w-4 text-slate-500" />
                          )}
                          <span className="sr-only">
                            {showPassword ? t('forgotPassword.hidePassword') : t('forgotPassword.showPassword')}
                          </span>
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('forgotPassword.confirmPassword')}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder={t('forgotPassword.confirmPasswordPlaceholder')}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="pl-10 pr-10 h-11"
                          required
                          minLength={8}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={toggleConfirmPasswordVisibility}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4 text-slate-500" />
                          ) : (
                            <Eye className="h-4 w-4 text-slate-500" />
                          )}
                          <span className="sr-only">
                            {showConfirmPassword ? t('forgotPassword.hidePassword') : t('forgotPassword.showPassword')}
                          </span>
                        </Button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white" 
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('forgotPassword.resetting')}
                        </>
                      ) : t('forgotPassword.resetButton')}
                    </Button>
                    <div className="text-center text-sm text-slate-600 dark:text-slate-400">
                      {t('forgotPassword.rememberPassword')}{' '}
                      <Link href="/login" className="text-blue-600 hover:underline dark:text-blue-400">
                        {t('forgotPassword.signIn')}
                      </Link>
                    </div>
                  </div>
                )}
              </CardContent>
            </form>
            
            {otpSent && (
              <div className="px-6 pb-6 text-center">
                <Button 
                  type="button" 
                  variant="ghost" 
                  className="text-sm text-blue-600 hover:bg-transparent hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  onClick={() => setOtpSent(false)}
                  disabled={loading}
                >
                  <ArrowLeft className="mr-1 h-4 w-4" /> {t('forgotPassword.backToEmail')}
                </Button>
              </div>
            )}
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
         
  );
}
